import React from 'react';
import { UseQueryState } from 'urql';

import {
  Cart,
  CartQuery,
  UpdateBillingAddressInput,
  UpdateShippingAddressInput,
} from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { ActiveCart, ActiveCartWithoutPaymentMethods } from 'types/cart';
import { transformShipmentMethod } from 'utils/payments/apple';

export type UseApplePayProps = {
  cartResult: UseQueryState<CartQuery>;
};

export const useApplePay = (params: UseApplePayProps) => {
  const t = useTranslate();

  const { countryCode } = useSiteConfig();

  const cart = params.cartResult.data?.activeCart;

  const totalText = `Puma ${countryCode.toLocaleUpperCase()}`;

  const createLineItems = React.useCallback(
    (
      cart: ActiveCart | ActiveCartWithoutPaymentMethods | Cart
    ): ApplePayJS.ApplePayShippingMethodUpdate => {
      return {
        newLineItems: [
          {
            label: t('applePayItemsTotal'),
            amount: (cart?.subTotal ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('applePayTax'),
            amount: (cart?.taxTotal ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('applePayShipment'),
            amount: (cart?.shipment?.shipmentMethod?.price ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('orderDiscount'),
            amount: (cart?.orderAdjustments || [])
              .reduce((sum, item) => sum + (item.price ?? 0), 0)
              .toFixed(2),
            type: 'final',
          },
          {
            label: t('appleShippingDiscount'),
            amount: (cart?.shippingAdjustments || [])
              .reduce((sum, item) => sum + (item.price ?? 0), 0)
              .toFixed(2),
            type: 'final',
          },
        ],
        newTotal: {
          label: totalText,
          amount: (cart?.estimatedTotal ?? 0).toFixed(2),
        },
      };
    },
    [t, totalText]
  );

  const mapAppleEventToAddressInput = (
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent
  ): [UpdateShippingAddressInput, UpdateBillingAddressInput] => {
    const billingContact = event.payment
      .billingContact as Required<ApplePayJS.ApplePayPaymentContact>;
    const shippingContact = event.payment
      .shippingContact as Required<ApplePayJS.ApplePayPaymentContact>;

    const basketId = cart?.id as string;

    return [
      {
        address1: shippingContact.addressLines[0],
        address2: shippingContact.addressLines[1],
        basketId,
        city: shippingContact.locality,
        countryCode: shippingContact.countryCode.toUpperCase(),
        firstName: shippingContact.givenName,
        lastName: shippingContact.familyName,
        phone: shippingContact.phoneNumber,
        postalCode: shippingContact.postalCode,
        stateCode: shippingContact.administrativeArea,
        email: shippingContact.emailAddress || billingContact.emailAddress,
        useAsBilling: false,
      },
      {
        address1: billingContact.addressLines[0],
        address2: billingContact.addressLines[1],
        basketId,
        city: billingContact.locality,
        countryCode: billingContact.countryCode.toUpperCase(),
        firstName: billingContact.givenName,
        lastName: billingContact.familyName,
        postalCode: billingContact.postalCode,
        stateCode: billingContact.administrativeArea,
        phone: billingContact.phoneNumber || shippingContact.phoneNumber,
      },
    ];
  };

  /**
   * Called when the the `ApplePayJS.ApplePayShippingContactSelectedEvent` is
   * successful. *
   */
  const getShippingContactUpdateForSuccess = (params: {
    addressResult: ActiveCart | ActiveCartWithoutPaymentMethods | Cart;
  }): ApplePayJS.ApplePayShippingContactUpdate => {
    return {
      ...createLineItems(params.addressResult),
      newShippingMethods:
        params.addressResult?.shipmentMethodInfo?.availableShipmentMethods.map(
          transformShipmentMethod
        ),
    };
  };

  /**
   * Called when the the `ApplePayJS.ApplePayShippingContactSelectedEvent` is
   * missing the fields required for tax calculation.
   *
   */
  const getShippingContactUpdateForMissingTaxFields =
    (): ApplePayJS.ApplePayShippingContactUpdate => {
      return {
        errors: [
          new ApplePayError(
            'shippingContactInvalid',
            'postalCode',
            t('applePayError')
          ),
        ],
        newTotal: {
          label: totalText,
          amount: (cart?.estimatedTotal ?? 0).toFixed(2),
        },
      };
    };

  const getShippingContactUpdateForShippingAddressMutationError =
    (): ApplePayJS.ApplePayShippingContactUpdate => {
      return {
        errors: [
          new ApplePayError(
            'shippingContactInvalid',
            'addressLines',
            'Postal code error'
          ),
        ],
        newTotal: {
          label: totalText,
          amount: (cart?.estimatedTotal ?? 0).toFixed(2),
        },
      };
    };

  // Apple does not give us (for privacy && sec concerns) the address1 and names.
  // Only when the fingerprint is recognised (oncompletepayment).
  // We need to update the address regardless, because of taxes.
  // But then we and up with Dummy values in the UI.
  // Same thing as with PayPal, so if payment is cancelled, we transfer carts.
  const generateUpdateShippingAddressInputFromAppleEvent = (
    event: ApplePayJS.ApplePayShippingContactSelectedEvent
  ): UpdateShippingAddressInput => {
    return {
      address1: 'Temporary',
      basketId: cart!.id,
      city: event.shippingContact.locality || '',
      countryCode: event.shippingContact?.countryCode?.toUpperCase() ?? '',
      firstName: event.shippingContact.givenName || 'Temporary',
      lastName: event.shippingContact.familyName || 'Temporary',
      phone: 'Temporary',
      postalCode: event.shippingContact.postalCode || '',
      stateCode: event.shippingContact.administrativeArea || '',
      useAsBilling: true,
    };
  };

  return {
    createLineItems,
    getShippingContactUpdateForMissingTaxFields,
    generateUpdateShippingAddressInputFromAppleEvent,
    getShippingContactUpdateForSuccess,
    getShippingContactUpdateForShippingAddressMutationError,
    mapAppleEventToAddressInput,
  };
};
